import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'

import TablePagination from 'src/v3/components/table-pagination'
import CardSectionHeader from 'src/v3/components/card-section-header'
import type { HydratedSessionV3 } from '../events-v2.slice'
import { isSessionPast } from '../utils/access-window'
import SessionRow from './session-row'
import type { SessionAttention } from './session-row'
import SessionRowSkeleton from './session-row-skeleton'
import tabLabel from './tab-label'

type TabValue = 'upcoming' | 'attention' | 'completed'

type AsyncStatus = 'uninitialized' | 'pending' | 'fulfilled' | 'rejected'

type SessionsPanelProps = {
  sessions: HydratedSessionV3[]
  status: AsyncStatus
  // The one session whose CE requirements are still outstanding, or null.
  attention: (SessionAttention & { sessionId: string }) | null
  // Sessions that met every completion requirement, across all attempts. null when the API
  // doesn't provide it yet; Completed then falls back to every attended session.
  completedSessionIDs: string[] | null
  onRetry: () => void
  onSuccess: (msg: string) => void
  onError: (msg: string) => void
}

let emptyMessages: Record<TabValue, string> = {
  upcoming: 'No upcoming sessions for this event',
  attention: 'No sessions require attention',
  completed: 'No completed sessions for this event',
}

let tabSx = { minHeight: 36, py: 0, textTransform: 'none', fontSize: 13 }

let SessionsPanel = ({
  sessions,
  status,
  attention,
  completedSessionIDs,
  onRetry,
  onSuccess,
  onError,
}: SessionsPanelProps) => {
  let [tab, setTab] = useState<TabValue>('upcoming')
  let [page, setPage] = useState(1)
  let [pageSize, setPageSize] = useState(10)

  let sessionKey = sessions
    .map((s) => `${s.id}:${s.invites?.[0]?.status ?? ''}`)
    .join(',')

  useEffect(() => {
    setPage(1)
  }, [sessionKey])

  let upcomingSessions = sessions.filter(
    (s) =>
      !isSessionPast(s.sessionTime, s.endTime) &&
      !s.invites.some((i) => i.status === 'attended'),
  )
  let attentionSessions = sessions.filter((s) => s.id === attention?.sessionId)
  let completedSessions = sessions
    .filter((s) =>
      completedSessionIDs != null
        ? completedSessionIDs.includes(s.id)
        : s.id !== attention?.sessionId &&
          s.invites.some((i) => i.status === 'attended'),
    )
    .toReversed()

  let tabSessions: Record<TabValue, HydratedSessionV3[]> = {
    upcoming: upcomingSessions,
    attention: attentionSessions,
    completed: completedSessions,
  }
  // The conditional tabs only render while they have sessions, e.g. Requires attention
  // disappears once the survey is submitted. Fall back to Upcoming when the selection vanishes.
  let showTab = (value: TabValue) =>
    value === 'upcoming' ||
    (status === 'fulfilled' && tabSessions[value].length > 0)
  let activeTab: TabValue = showTab(tab) ? tab : 'upcoming'
  let activeSessions = tabSessions[activeTab]
  let pagedSessions = activeSessions.slice(
    (page - 1) * pageSize,
    page * pageSize,
  )

  let handleTabChange = (_: React.SyntheticEvent, value: TabValue) => {
    setTab(value)
    setPage(1)
  }

  return (
    <Box>
      <CardSectionHeader label="Sessions" className="mb-3" />
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ mb: 2, minHeight: 36 }}
        slotProps={{ indicator: { style: { height: 2 } } }}
      >
        <Tab
          value="upcoming"
          label={tabLabel(
            'Upcoming',
            status === 'fulfilled' ? upcomingSessions.length : '-',
            'text.secondary',
          )}
          sx={tabSx}
        />
        {showTab('attention') && (
          <Tab
            value="attention"
            label={tabLabel(
              'Requires attention',
              attentionSessions.length,
              'warning.main',
            )}
            sx={tabSx}
          />
        )}
        {showTab('completed') && (
          <Tab
            value="completed"
            label={tabLabel(
              'Completed',
              completedSessions.length,
              'success.main',
            )}
            sx={tabSx}
          />
        )}
      </Tabs>
      {status === 'rejected' ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Could not load sessions.
        </Alert>
      ) : status === 'pending' || status === 'uninitialized' ? (
        <Box className="flex flex-col">
          <span className="sr-only" role="status" aria-live="polite">
            Loading sessions…
          </span>
          {Array.from({ length: pageSize }).map((_, i) => (
            <Box key={i} aria-hidden="true">
              <SessionRowSkeleton showThumbnail={false} />
            </Box>
          ))}
        </Box>
      ) : pagedSessions.length > 0 ? (
        <Box className="flex flex-col gap-3">
          {pagedSessions.map((s) => (
            <SessionRow
              key={s.id}
              sessionId={s.id}
              onSuccess={onSuccess}
              onError={onError}
              disableCardLink
              showThumbnail={false}
              attention={
                activeTab === 'attention' && attention != null
                  ? attention
                  : undefined
              }
            />
          ))}
        </Box>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          className="py-4 text-center"
        >
          {emptyMessages[activeTab]}
        </Typography>
      )}
      {status === 'fulfilled' && (
        <TablePagination
          total={activeSessions.length}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
          itemLabel="sessions"
        />
      )}
    </Box>
  )
}

export default SessionsPanel
